/**
 * TanStack Query hooks for `GET/POST/PUT /notifications*` (project brief §21).
 *
 * `unread-count` is polled (the bell needs to notice a new notification
 * without the person doing anything) with a sensible interval, paused while
 * the tab is hidden, and refetched on window focus so switching back to the
 * tab never shows a stale badge.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationDto,
  NotificationPreferencesResponseDto,
  NotificationUnreadCountDto,
  PaginatedDto,
  UpdateNotificationPreferencesDto,
} from '@ithelpdesk/shared';
import { apiGet, apiPost, shouldRetryQuery, NO_MUTATION_RETRY } from './client';

const UNREAD_POLL_INTERVAL_MS = 30_000;

export const notificationQueryKeys = {
  all: () => ['notifications'] as const,
  list: (params: NotificationListParams) => ['notifications', 'list', params] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
  preferences: () => ['notifications', 'preferences'] as const,
};

export interface NotificationListParams {
  cursor?: string;
  limit?: number;
  unreadOnly?: boolean;
}

function toQuery(params: NotificationListParams): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set('cursor', params.cursor);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.unreadOnly) search.set('unreadOnly', 'true');
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** Tracks page visibility so polling hooks below can back off while the tab is hidden — no point hammering the API for a badge nobody can see. */
export function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  useEffect(() => {
    function onChange(): void {
      setVisible(document.visibilityState === 'visible');
    }
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/** `GET /notifications/unread-count` — polled for the bell badge, refetched on focus, backed off while the tab is hidden. */
export function useUnreadNotificationCount() {
  const visible = usePageVisible();
  return useQuery({
    queryKey: notificationQueryKeys.unreadCount(),
    queryFn: ({ signal }) => apiGet<NotificationUnreadCountDto>('/notifications/unread-count', { signal }),
    retry: shouldRetryQuery,
    refetchInterval: visible ? UNREAD_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export function useNotificationList(params: NotificationListParams = {}) {
  return useQuery({
    queryKey: notificationQueryKeys.list(params),
    queryFn: ({ signal }) => apiGet<PaginatedDto<NotificationDto>>(`/notifications${toQuery(params)}`, { signal }),
    retry: shouldRetryQuery,
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationQueryKeys.preferences(),
    queryFn: ({ signal }) => apiGet<NotificationPreferencesResponseDto>('/notifications/preferences', { signal }),
    retry: shouldRetryQuery,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (id: string) => apiPost<{ id: string; readAt: string }>(`/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: () => apiPost<{ updated: number }>('/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all() });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (dto: UpdateNotificationPreferencesDto) => apiPost<NotificationPreferencesResponseDto>('/notifications/preferences', dto),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationQueryKeys.preferences(), data);
    },
  });
}
